import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  Badge,
  useColorModeValue,
  Heading,
  Card,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalCloseButton,
  ModalBody,
  useDisclosure,
  Input,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Select,
  useToast,
} from '@chakra-ui/react';
import { AddIcon, EditIcon, DeleteIcon, ViewIcon, RepeatIcon, SmallCloseIcon } from '@chakra-ui/icons';
import { MdPowerSettingsNew } from 'react-icons/md';
import { Icon } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { useState as reactUseState, useEffect, useRef } from 'react';
import { associateAPI } from '../services/api';
import { ArrowDownIcon, DownloadIcon, AttachmentIcon } from '@chakra-ui/icons';

// import { IconButton } from "@chakra-ui/react";
import { HiSortAscending } from "react-icons/hi"  
// Define the Associate interface
interface Associate {
  _id?: string;
  name: string;
  email: string;
  phone?: string;
  department: string;
  designation: string;
  joinDate: string;
  status: string;
  skills?: string;
  emergencyContact?: {
    name: string;
    contactNumber: string;
  };
}

const Associates: React.FC = () => {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [viewingAssociate, setViewingAssociate] = useState<Associate | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [associates, setAssociates] = useState<Associate[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [isSortOpen, setIsSortOpen] = useState(false);
  const [sortField, setSortField] = useState<'name'|'joinDate'|'department'|'designation'|''>('');
  const [sortOrder, setSortOrder] = useState<'asc'|'desc'>('asc');
  const [designations, setDesignations] = useState<any[]>([]);
  const toast = useToast();
  const { isOpen: isUploadOpen, onOpen: onUploadOpen, onClose: onUploadClose } = useDisclosure();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadResults, setUploadResults] = useState<any[] | null>(null);
  const { isOpen: isResultsOpen, onOpen: onResultsOpen, onClose: onResultsClose } = useDisclosure();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const inputBg = useColorModeValue('gray.50', 'gray.700');

  useEffect(() => {
    // fetch designations once on mount
    fetchDesignations();
  }, []);

  // Debounced incremental search: trigger server search 300ms after user stops typing
  useEffect(() => {
    const handler = setTimeout(() => {
      fetchAssociates();
    }, 300);
    return () => clearTimeout(handler);
    // only re-run when searchTerm, sortField, or sortOrder change
  }, [searchTerm, sortField, sortOrder]);

  const fetchAssociates = async () => {
    try {
      setIsSearching(true);
      const q = (searchTerm || '').toString().trim();
      // Prefer the sorted API if sorting is active
      const res = (sortField)
        ? await associateAPI.getAllAssociatesSorted({ searchTerm: q, sortField, sortOrder })
        : await associateAPI.getAllAssociates(q);
      setAssociates(res.data);
      setIsSearching(false);
    } catch (err: any) {
      console.error('Fetch Associates Error:', err.message || err);
      toast({
        title: 'Error',
        description: 'Failed to fetch associates. Please ensure the backend server is running.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      setAssociates([]);
  setIsSearching(false);
    }
  };

  const fetchDesignations = async () => {
    try {
      // Get JWT token from localStorage
      const token = localStorage.getItem('token');
      const res = await fetch((process.env.REACT_APP_API_URL || 'http://localhost:5000/api') + '/designations', {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      });
      const data = await res.json();
      setDesignations(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch designations', err);
      toast({
        title: 'Error',
        description: 'Failed to fetch designations. Department names may not display correctly.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const getDepartmentName = (departmentId: string) => {
    const designation = designations.find(d => String(d._id) === String(departmentId));
    return designation?.department || departmentId || 'N/A';
  };

  const toggleStatus = async (associate: Associate) => {
    const id = associate._id;
    if (!id) {
      toast({
        title: 'Missing ID',
        description: 'Associate id is missing. Cannot update status.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }
    const newStatus = associate.status === 'active' ? 'inactive' : 'active';
    try {
      await associateAPI.updateAssociate(id, { status: newStatus });
      setAssociates(prev => prev.map(a => (a._id === id ? { ...a, status: newStatus } : a)));
      toast({
        title: 'Status Updated',
        description: `${associate.name} is now ${newStatus}.`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (err: any) {
      console.error('Update status error:', err.message || err);
      toast({
        title: 'Failed to Update Status',
        description: 'Failed to update status. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const handleAddAssociate = () => {
    navigate('/associates/add');
  };

  const handleEditAssociate = async (associate: Associate) => {
    if (!associate._id) {
      toast({
        title: 'Missing ID',
        description: 'Cannot edit associate without an id.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      return;
    }

    try {
      const res = await associateAPI.getAssociateById(associate._id);
      setViewingAssociate(res.data);
      navigate(`/associates/edit/${associate._id}`);
    } catch (err: any) {
      console.error('Fetch Associate by ID Error:', err.message);
      toast({
        title: 'Failed to Fetch Associate',
        description: 'Failed to fetch associate details. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const handleDeleteAssociate = async (id: string) => {
    try {
      await associateAPI.softDeleteAssociate(id);
      toast({
        title: 'Associate Deleted',
        description: 'Associate deleted successfully.',
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
      fetchAssociates();
    } catch (err: any) {
      console.error('Delete Associate Error:', err.message);
      toast({
        title: 'Failed to Delete Associate',
        description: 'Failed to delete associate. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'green';
      case 'inactive':
        return 'gray';
      case 'onboarding':
        return 'blue';
      default:
        return 'gray';
    }
  };

  const formatPhone = (phone: string | undefined) => {
    if (!phone || !phone.startsWith('+')) return phone || '';
    const countryCodes = ['+1', '+44', '+91', '+61', '+49', '+33', '+81'].sort((a, b) => b.length - a.length);
    for (const code of countryCodes) {
      if (phone.startsWith(code)) {
        return `${code}-${phone.slice(code.length)}`;
      }
    }
    return phone;
  };

  const filteredAssociates = associates;

  return (
    <Box w="full" px={{ base: 2, md: 6 }}>
      <Box mb={8}>
        <Heading size="lg" color="gray.800" mb={2}>
          Associates
        </Heading>
        <Text color="gray.600">
          Onboard and manage your team members
        </Text>
      </Box>

      <Box mb={6}>
        <VStack
          spacing={4}
          align={{ base: 'stretch', md: 'center' }}
          flexDir={{ base: 'column', md: 'row' }}
        >
          <Button
            leftIcon={<AddIcon />}
            colorScheme="primary"
            onClick={handleAddAssociate}
            className="btn-primary"
            w={{ base: '100%', md: 'auto' }}
            borderRadius="md"
            px={6}
            _hover={{ bg: 'primary.600', transform: 'translateY(-1px)' }}
            _active={{ bg: 'primary.700' }}
          >
            Onboard Associate
          </Button>
          {/* <Box w={{ base: '100%', md: 'auto' }} minW={{ md: '500px' }} maxW={{ md: '400px' }}>
            <HStack>
                <Input
                  placeholder="Search by name, email, department, designation, or ID..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="form-input"
                  w="100%"
                  borderRadius="md"
                  bg={inputBg}
                />
                <Box minW="40px">
                  <Text fontSize="sm" color="gray.500">{isSearching ? 'Searching...' : ''}</Text>
                </Box> */}

                <Box
  w={{ base: '100%', md: 'auto' }}
  minW={{ md: '600px' }}
  maxW={{ md: '700px' }} // Increased width
>
  <HStack>
    <Input
      placeholder="Search by Id, name, email, department, designation"
      value={searchTerm}
      onChange={(e) => setSearchTerm(e.target.value)}
      className="form-input"
      w="100%"
      borderRadius="md"
      bg={inputBg}
    />
    {/* <Box minW="40px">
      <Text fontSize="sm" color="gray.500">
        {isSearching ? 'Searching...' : ''}
      </Text>
    </Box> */}
  {/* </HStack> */}
{/* </Box> */}

              <IconButton
                aria-label="Sort"
                icon={<ArrowDownIcon />}
                variant="ghost"
                onClick={() => setIsSortOpen(true)}
                title="Sort"
                borderRadius="md"
                _hover={{ bg: 'gray.100', transform: 'translateY(-1px)' }}
              />
              <IconButton
                aria-label="Upload CSV file"
                icon={<AttachmentIcon />}
                variant="ghost"
                onClick={() => onUploadOpen()}
                title="Upload CSV file"
                borderRadius="md"
                _hover={{ bg: 'gray.100', transform: 'translateY(-1px)' }}
              />
            </HStack>
          </Box>
        </VStack>
      </Box>

      <Box overflowX="auto" w="full">
        <Card bg={cardBg} border="1px" borderColor={borderColor}>
          <CardBody p={0}>
            <Table variant="simple" minWidth="900px">
              <Thead>
                <Tr>
                  <Th className="table-header">Name</Th>
                  <Th className="table-header">Department</Th>
                  <Th className="table-header">Designation</Th>
                  <Th className="table-header">Join Date</Th>
                  <Th className="table-header text-center" textAlign="center">Status</Th>
                  <Th className="table-header">Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filteredAssociates.length === 0 ? (
                  <Tr>
                    <Td colSpan={6} textAlign="center">
                      <Text color="gray.500">No associates found</Text>
                    </Td>
                  </Tr>
                ) : (
                  filteredAssociates.map((associate, index) => (
                    <Tr key={associate._id || `temp-${index}`}>
                      <Td className="table-cell">
                        <VStack align="start" spacing={1}>
                          <Text fontWeight="medium">{associate.name}</Text>
                          <Text fontSize="sm" color="gray.500">
                            ID:{associate._id}
                          </Text>
                        </VStack>
                      </Td>
                      <Td className="table-cell">{getDepartmentName(associate.department)}</Td>
                      <Td className="table-cell">
                        {(() => {
                          const designationObj = designations.find(d => String(d._id) === String(associate.designation));
                          return designationObj ? designationObj.title : associate.designation;
                        })()}
                      </Td>
                      <Td className="table-cell">{associate.joinDate}</Td>
                      <Td className="table-cell text-center" textAlign="center">
                        <Badge
                          colorScheme={getStatusColor(associate.status)}
                          variant="subtle"
                          px={3}
                          py={1}
                          textTransform="capitalize"
                          fontSize="sm"
                          borderRadius="md"
                          minW="80px"
                          textAlign="center"
                          display="inline-block"
                        >
                          {associate.status}
                        </Badge>
                      </Td>
                      <Td className="table-cell">
                        <HStack spacing={2} flexWrap="nowrap">
                          <IconButton
                            aria-label="Toggle status"
                            icon={<RepeatIcon />}
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleStatus(associate)}
                            title={associate.status === 'active' ? 'Deactivate' : 'Activate'}
                            sx={{ color: associate.status === 'active' ? 'red' : 'green' }}
                            borderRadius="md"
                            _hover={{ bg: 'gray.100', transform: 'translateY(-1px)' }}
                          />
                          <IconButton
                            aria-label="View associate"
                            icon={<ViewIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => { setViewingAssociate(associate); onOpen(); }}
                            borderRadius="md"
                            _hover={{ bg: 'gray.100', transform: 'translateY(-1px)' }}
                          />
                          <IconButton
                            aria-label="Edit associate"
                            icon={<EditIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() => associate._id ? handleEditAssociate(associate) : toast({ title: 'Missing ID', description: 'Cannot edit associate without an id.', status: 'error', duration: 3000, isClosable: true, position: 'top-right' })}
                            borderRadius="md"
                            _hover={{ bg: 'gray.100', transform: 'translateY(-1px)' }}
                          />
                          <IconButton
                            aria-label="Delete associate"
                            icon={<DeleteIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() => associate._id ? handleDeleteAssociate(associate._id) : toast({ title: 'Missing ID', description: 'Cannot delete associate without an id.', status: 'error', duration: 3000, isClosable: true, position: 'top-right' })}
                            borderRadius="md"
                            _hover={{ bg: 'gray.100', transform: 'translateY(-1px)' }}
                          />
                        </HStack>
                      </Td>
                    </Tr>
                  ))
                )}
              </Tbody>
            </Table>
          </CardBody>
        </Card>
      </Box>

      <Modal isOpen={isOpen} onClose={onClose} size={{ base: 'xs', sm: 'md' }}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Associate Details</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {viewingAssociate && (
              <VStack align="start" spacing={3} fontSize={{ base: 'sm', md: 'md' }}>
                <Text><b>Name:</b> {viewingAssociate.name}</Text>
                <HStack spacing={1} flexWrap="wrap">
                  <Text fontWeight="bold">Email:</Text>
                  <Menu>
                    <MenuButton as={Text} color="blue.500" textDecoration="underline" cursor="pointer">
                      {viewingAssociate.email}
                    </MenuButton>
                    <MenuList>
                      <MenuItem as="a" href={`mailto:${viewingAssociate.email}`}>Default Mail Client</MenuItem>
                      <MenuItem as="a" href={`https://mail.google.com/mail/?view=cm&fs=1&to=${viewingAssociate.email}`} target="_blank">Gmail</MenuItem>
                      <MenuItem as="a" href={`https://outlook.live.com/mail/0/deeplink/compose?to=${viewingAssociate.email}`} target="_blank">Outlook</MenuItem>
                    </MenuList>
                  </Menu>
                </HStack>
                {viewingAssociate.phone && (
                  <Text><b>Phone Number:</b> {formatPhone(viewingAssociate.phone)}</Text>
                )}
                <Text><b>Department:</b> {getDepartmentName(viewingAssociate.department)}</Text>
               <Text><b>Designation:</b> {
                  (() => {
                    if (!viewingAssociate) return '';
                    const designationObj = designations.find(d => String(d._id) === String(viewingAssociate.designation));
                    return designationObj ? designationObj.title : viewingAssociate.designation;
                  })()
                }</Text>
                <Text><b>Status:</b> {viewingAssociate.status}</Text>
                <Text><b>Join Date:</b> {viewingAssociate.joinDate}</Text>
                {viewingAssociate.skills && (
                  <Text><b>Skills:</b> {viewingAssociate.skills}</Text>
                )}
                {viewingAssociate.emergencyContact && (
                  <Box mt={2}>
                    <Text fontWeight="bold">Emergency Contact:</Text>
                    <Text>Name: {viewingAssociate.emergencyContact.name}</Text>
                    <Text>Contact Number: {formatPhone(viewingAssociate.emergencyContact.contactNumber)}</Text>
                  </Box>
                )}
              </VStack>
            )}
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Sort Modal */}
      <Modal isOpen={isSortOpen} onClose={() => setIsSortOpen(false)} size="xs">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Sort Associates</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="start" spacing={2}>
              <Text fontWeight="semibold" fontSize="sm">Field</Text>
              <Select value={sortField} onChange={(e) => setSortField(e.target.value as 'name'|'joinDate'|'department'|'designation'|'')}>
                <option value="">None</option>
                <option value="name">Name</option>
                <option value="joinDate">Join Date</option>
                <option value="department">Department</option>
                <option value="designation">Designation</option>
              </Select>

              <Text fontWeight="semibold" fontSize="sm">Order</Text>
              <Select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as 'asc'|'desc')}>
                <option value="asc">Ascending ↑</option>
                <option value="desc">Descending ↓</option>
              </Select>

              <HStack pt={2} w="full" justify="end">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setSortField(''); setSortOrder('asc'); setIsSortOpen(false); fetchAssociates(); }}
                  borderRadius="md"
                  _hover={{ bg: 'gray.100', transform: 'translateY(-1px)' }}
                >
                  Clear
                </Button>
                <Button
                  size="sm"
                  colorScheme="primary"
                  onClick={() => { setIsSortOpen(false); fetchAssociates(); }}
                  borderRadius="md"
                  _hover={{ bg: 'primary.600', transform: 'translateY(-1px)' }}
                  _active={{ bg: 'primary.700' }}
                >
                  Apply
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Upload Modal */}
      <Modal isOpen={isUploadOpen} onClose={() => { setSelectedFile(null); onUploadClose(); }} size="sm">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Import Associates</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <VStack align="stretch" spacing={4}>
              <Text fontWeight="semibold" fontSize="sm">Upload CSV/XLSX</Text>
              <HStack w="full" spacing={3} align="center">
                <Button
                  size="sm"
                  leftIcon={<AttachmentIcon />}
                  colorScheme="primary"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  borderRadius="md"
                  px={4}
                  _hover={{ bg: 'primary.50', transform: 'translateY(-1px)' }}
                  _active={{ bg: 'primary.100' }}
                >
                  Choose File
                </Button>
                <Input
                  type="file"
                  accept=".csv,.xlsx"
                  ref={fileInputRef}
                  display="none"
                  onChange={(e) => {
                    const f = e.target.files?.[0] || null;
                    if (f && !/\.(csv|xlsx)$/i.test(f.name)) {
                      toast({
                        title: 'Invalid file',
                        description: 'Only .csv and .xlsx files are allowed.',
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                        position: 'top-right',
                      });
                      e.target.value = '';
                      setSelectedFile(null);
                      return;
                    }
                    setSelectedFile(f);
                  }}
                />
                {selectedFile && (
                  <HStack flex={1} bg={inputBg} borderRadius="md" px={3} py={2} border="1px" borderColor={borderColor}>
                    <Text fontSize="sm" color="gray.600" isTruncated>
                      {selectedFile.name}
                    </Text>
                    <IconButton
                      aria-label="Remove file"
                      icon={<SmallCloseIcon />}
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                      }}
                      _hover={{ bg: 'gray.100', transform: 'translateY(-1px)' }}
                    />
                  </HStack>
                )}
              </HStack>

              <HStack w="full" justify="space-between" align="center">
                <Text fontWeight="semibold" fontSize="sm">Download Template</Text>
                <Button
                  size="sm"
                  leftIcon={<DownloadIcon />}
                  variant="outline"
                  borderRadius="md"
                  px={4}
                  _hover={{ bg: 'gray.50', transform: 'translateY(-1px)' }}
                  _active={{ bg: 'gray.100' }}
                  onClick={async () => {
                    try {
                      const res = await associateAPI.downloadTemplate();
                      const blob = new Blob([res.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
                      const url = window.URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = 'associate_template.xlsx';
                      a.click();
                      window.URL.revokeObjectURL(url);
                    } catch (err: any) {
                      console.error('Template download error', err);
                      toast({
                        title: 'Error',
                        description: 'Failed to download template.',
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                        position: 'top-right',
                      });
                    }
                  }}
                >
                  Download
                </Button>
              </HStack>

              <HStack w="full" justify="end" pt={2}>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => { setSelectedFile(null); onUploadClose(); }}
                  borderRadius="md"
                  _hover={{ bg: 'gray.100', transform: 'translateY(-1px)' }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  colorScheme="primary"
                  onClick={async () => {
                    if (!selectedFile) {
                      toast({
                        title: 'No file',
                        description: 'Please select a file to upload.',
                        status: 'warning',
                        duration: 2500,
                        isClosable: true,
                        position: 'top-right',
                      });
                      return;
                    }
                    try {
                      const res = await associateAPI.uploadAssociates(selectedFile);
                      const msg = String(res.data?.message || 'Import finished');
                      const resultsArray = Array.isArray(res.data?.results) ? res.data.results : [];
                      const errorRows = resultsArray.filter((r: any) => (r.errors && r.errors.length) || r.action === 'error' || r.action === 'skipped');
                      if (errorRows.length > 0) {
                        toast({
                          title: 'Import Completed with Errors',
                          description: `${errorRows.length} row(s) had validation issues. Please review the import results.`,
                          status: 'error',
                          duration: 5000,
                          isClosable: true,
                          position: 'top-right',
                        });
                        // Keep upload modal open so user can correct file; still show detailed results
                        if (res.data && Array.isArray(res.data.results)) {
                          setUploadResults(res.data.results);
                          onResultsOpen();
                        }
                        // do not clear selectedFile or close modal so user can retry
                        return;
                      } else {
                        toast({
                          title: 'Import Complete',
                          description: msg,
                          status: 'success',
                          duration: 3000,
                          isClosable: true,
                          position: 'top-right',
                        });
                        setSelectedFile(null);
                        if (fileInputRef.current) fileInputRef.current.value = '';
                        onUploadClose();
                        fetchAssociates();
                        if (res.data && Array.isArray(res.data.results)) {
                          setUploadResults(res.data.results);
                          onResultsOpen();
                        }
                      }
                    } catch (err: any) {
                      console.error('Upload error', err);
                      const message = err?.response?.data?.message || 'Failed to upload file';
                      toast({
                        title: 'Upload Failed',
                        description: message,
                        status: 'error',
                        duration: 3000,
                        isClosable: true,
                        position: 'top-right',
                      });
                      return;
                    }
                  }}
                  borderRadius="md"
                  px={6}
                  _hover={{ bg: 'primary.600', transform: 'translateY(-1px)' }}
                  _active={{ bg: 'primary.700' }}
                >
                  Upload
                </Button>
              </HStack>
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>

      {/* Upload Results Modal */}
      <Modal isOpen={isResultsOpen} onClose={() => { setUploadResults(null); onResultsClose(); }} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Import Results</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack align="start" spacing={3}>
              {uploadResults && uploadResults.length === 0 && (
                <Text>No issues found. All rows imported.</Text>
              )}
              {uploadResults && uploadResults.map((r, idx) => (
                <Box key={idx} w="full" p={2} borderWidth={1} borderRadius="md">
                  <Text fontSize="sm"><b>Row:</b> {r.row ?? '-' } <b>Action:</b> {r.action}</Text>
                  {r.errors && r.errors.length > 0 && (
                    <VStack align="start" spacing={1} mt={1}>
                      {r.errors.map((e: string, i: number) => (
                        <Text key={i} fontSize="sm" color="red.600">{e}</Text>
                      ))}
                    </VStack>
                  )}
                  {r.message && <Text fontSize="sm" color="red.600">{r.message}</Text>}
                </Box>
              ))}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

function useState<T>(initialValue: T): [T, React.Dispatch<React.SetStateAction<T>>] {
  return reactUseState(initialValue);
}

export default Associates;